// League workspace — Schedule Management view.
//
// Landing page treatment:
//   • Gradient hero header with the three primary actions
//     (Add Match / Upload Excel / Template) grouped as a CTA cluster.
//   • KPI strip: Total · Upcoming · This Weekend · Grounds — each an
//     accent-toned card so the season-at-a-glance reads in one scan.
//   • Filter toolbar: All / Upcoming / This Weekend / Past chips + a
//     free-text search across teams / series / ground / notes.
//   • Series sections — each rendered as a gradient banner header
//     (tone seeded from series name) followed by scoreboard-style
//     match cards laid out on a responsive grid.
//
// Data flow, modals, and Excel preview logic are unchanged from the
// previous plain-list version.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Plus, X, Pencil, Trash2, CalendarDays, Upload, FileSpreadsheet,
  MapPin, Clock, Trophy, Search, ListChecks, Sun, Filter, History,
  ArrowRight
} from "lucide-react";
import { Panel } from "../../components/primitives";
import { useToast } from "../../components/Toast";
import { api } from "../../api";
import { EMPTY_MATCH, GROUND_SUGGESTIONS, fmtMatchDate, fmt12 } from "./shared";

const TONE_PALETTE = ["gold", "cyan", "green", "violet", "orange"];
function toneFor(str = "") {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return TONE_PALETTE[h % TONE_PALETTE.length];
}

// ISO for today in local time — the schedule and its "upcoming"
// filter are always meaningful in the viewer's own time zone.
function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function upcomingWeekendIsos() {
  const now = new Date();
  const dow = now.getDay();
  const start = new Date(now); start.setHours(0, 0, 0, 0);
  let sat;
  if (dow === 6) sat = start;
  else if (dow === 0) sat = new Date(start.getTime() - 86400000);
  else { sat = new Date(start); sat.setDate(start.getDate() + (6 - dow)); }
  const sun = new Date(sat); sun.setDate(sat.getDate() + 1);
  const toIso = d => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };
  return { satIso: toIso(sat), sunIso: toIso(sun) };
}
function splitDate(iso) {
  if (!iso) return { day: "—", dow: "", mon: "" };
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return { day: iso, dow: "", mon: "" };
  return {
    day: String(d.getDate()),
    dow: d.toLocaleDateString("en-US", { weekday: "short" }),
    mon: d.toLocaleDateString("en-US", { month: "short" })
  };
}

export default function LeagueSchedule() {
  const toast = useToast();
  const [matches,   setMatches]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [busy,      setBusy]      = useState(false);

  const [showForm,  setShowForm]  = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form,      setForm]      = useState(EMPTY_MATCH);

  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadMsg,  setUploadMsg]  = useState("");
  const [preview,    setPreview]    = useState(null);
  const [uploadMode, setUploadMode] = useState("append");
  const fileRef = useRef(null);

  const [filter, setFilter] = useState("all"); // all | upcoming | weekend | past
  const [search, setSearch] = useState("");

  async function refresh() {
    setLoading(true);
    try {
      const rows = await api.listLeagueMatches();
      setMatches(rows);
    } catch (err) {
      toast.error(err.message || "Couldn't load schedule.");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!showForm && !preview) return;
    const onKey = e => {
      if (e.key !== "Escape") return;
      if (preview) setPreview(null);
      if (showForm) close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showForm, preview]);

  function openCreate() { setEditingId(null); setForm(EMPTY_MATCH); setShowForm(true); }
  function openEdit(m) {
    setEditingId(m.id);
    setForm({
      series:  m.series  || "",
      date:    m.date    || "",
      time:    m.time    || "",
      teamOne: m.teamOne || "",
      teamTwo: m.teamTwo || "",
      ground:  m.ground  || "",
      notes:   m.notes   || ""
    });
    setShowForm(true);
  }
  function close() { setShowForm(false); setEditingId(null); setForm(EMPTY_MATCH); }

  async function submit(e) {
    e.preventDefault();
    if (!form.series.trim())  { toast.error("Series is required."); return; }
    if (!form.date)           { toast.error("Match date is required."); return; }
    if (!form.teamOne.trim()) { toast.error("Team One is required."); return; }
    if (!form.teamTwo.trim()) { toast.error("Team Two is required."); return; }
    if (!form.ground.trim())  { toast.error("Ground is required."); return; }
    if (form.teamOne.trim().toLowerCase() === form.teamTwo.trim().toLowerCase()) {
      toast.error("Team One and Team Two must be different."); return;
    }
    setBusy(true);
    try {
      if (editingId) {
        await api.updateLeagueMatch(editingId, form);
        toast.success("Match updated.");
      } else {
        await api.createLeagueMatch(form);
        toast.success("Match added.");
      }
      close();
      await refresh();
    } catch (err) { toast.error(err.message || "Save failed."); }
    finally { setBusy(false); }
  }

  async function remove(m) {
    if (!confirm(`Delete ${m.teamOne} vs ${m.teamTwo} on ${m.date}?`)) return;
    try {
      await api.deleteLeagueMatch(m.id);
      toast.success("Match removed.");
      await refresh();
    } catch (err) { toast.error(err.message || "Delete failed."); }
  }

  async function onPickFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadBusy(true); setUploadMsg(""); setPreview(null);
    try {
      const data = await api.previewLeagueSchedule(file);
      setPreview(data);
    } catch (err) {
      setUploadMsg(err.message || "Preview failed.");
    } finally {
      setUploadBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }
  async function applyUpload() {
    if (!preview) return;
    setUploadBusy(true); setUploadMsg("");
    try {
      const r = await api.applyLeagueSchedule(preview.token, uploadMode);
      toast.success(`${r.inserted || 0} matches imported.`);
      setPreview(null);
      setUploadMode("append");
      await refresh();
    } catch (err) {
      setUploadMsg(err.message || "Import failed.");
    } finally { setUploadBusy(false); }
  }
  async function downloadTemplate() {
    try { await api.downloadLeagueScheduleTemplate(); }
    catch (err) { toast.error(err.message || "Template download failed."); }
  }

  // ── Derived stats + filtering ────────────────────────────────
  const today = useMemo(todayIso, []);
  const { satIso, sunIso } = useMemo(upcomingWeekendIsos, []);

  const stats = useMemo(() => {
    const upcoming = matches.filter(m => m.date >= today).length;
    const weekend  = matches.filter(m => m.date === satIso || m.date === sunIso).length;
    const grounds  = new Set(matches.map(m => (m.ground || "").trim()).filter(Boolean));
    const nextMatch = matches
      .filter(m => m.date >= today)
      .sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || "")))[0];
    return { upcoming, weekend, grounds, nextMatch };
  }, [matches, today, satIso, sunIso]);

  const filtered = useMemo(() => {
    let list = matches;
    if (filter === "upcoming") list = list.filter(m => m.date >= today);
    else if (filter === "past")     list = list.filter(m => m.date < today);
    else if (filter === "weekend")  list = list.filter(m => m.date === satIso || m.date === sunIso);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(m =>
        (m.series || "").toLowerCase().includes(q)   ||
        (m.teamOne || "").toLowerCase().includes(q)  ||
        (m.teamTwo || "").toLowerCase().includes(q)  ||
        (m.ground || "").toLowerCase().includes(q)   ||
        (m.notes  || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [matches, filter, search, today, satIso, sunIso]);

  const matchesBySeries = useMemo(() => {
    const map = new Map();
    for (const m of filtered) {
      if (!map.has(m.series)) map.set(m.series, []);
      map.get(m.series).push(m);
    }
    for (const [k, v] of map) {
      v.sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || "")));
      map.set(k, v);
    }
    return [...map.entries()];
  }, [filtered]);

  const FILTER_OPTS = [
    { key: "all",      label: "All",           icon: ListChecks, count: matches.length },
    { key: "upcoming", label: "Upcoming",      icon: ArrowRight, count: stats.upcoming },
    { key: "weekend",  label: "This Weekend",  icon: Sun,        count: stats.weekend },
    { key: "past",     label: "Past",          icon: History,    count: matches.length - stats.upcoming }
  ];

  const nextMatch = stats.nextMatch;

  return (
    <div className="stack league-view">
      {/* ── Hero header ────────────────────────────────────────── */}
      <header className="sched-hero">
        <div className="sched-hero-bg" aria-hidden="true"/>
        <div className="sched-hero-body">
          <div className="sched-hero-copy">
            <span className="sched-hero-eyebrow">
              <CalendarDays size={12}/> Pittsburgh Cricket League
            </span>
            <h2>Schedule Management</h2>
            <p>Plan, publish, and update every match of the season — bulk from Excel or one at a time.</p>
          </div>
          <div className="sched-hero-actions">
            <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={onPickFile}/>
            <button type="button" className="btn" onClick={openCreate} disabled={uploadBusy}>
              <Plus size={15}/> Add Match
            </button>
            <button type="button" className="sched-hero-btn"
              onClick={() => fileRef.current?.click()} disabled={uploadBusy}>
              <Upload size={14}/> {uploadBusy ? "Uploading…" : "Upload Excel"}
            </button>
            <button type="button" className="sched-hero-btn sched-hero-btn--ghost"
              onClick={downloadTemplate} disabled={uploadBusy}>
              <FileSpreadsheet size={14}/> Template
            </button>
          </div>
        </div>
        {nextMatch && (
          <div className="sched-hero-next">
            <span className="sched-hero-next-eyebrow">
              <Trophy size={11}/> Next up
            </span>
            <div className="sched-hero-next-body">
              <b>{nextMatch.teamOne}</b>
              <span>vs</span>
              <b>{nextMatch.teamTwo}</b>
              <span className="sched-hero-next-when">
                {fmtMatchDate(nextMatch.date)}
                {nextMatch.time ? ` · ${fmt12(nextMatch.time)} ET` : ""} · {nextMatch.ground}
              </span>
            </div>
          </div>
        )}
      </header>

      {uploadMsg && <div className="err">{uploadMsg}</div>}

      {/* ── KPI stats strip ───────────────────────────────────── */}
      <div className="sched-stats">
        <div className="sched-stat sched-stat--gold">
          <span className="sched-stat-icon"><CalendarDays size={14}/></span>
          <div>
            <small>Total matches</small>
            <b>{matches.length}</b>
            <em>on the schedule</em>
          </div>
        </div>
        <div className="sched-stat sched-stat--cyan">
          <span className="sched-stat-icon"><ArrowRight size={14}/></span>
          <div>
            <small>Upcoming</small>
            <b>{stats.upcoming}</b>
            <em>from today onward</em>
          </div>
        </div>
        <div className="sched-stat sched-stat--violet">
          <span className="sched-stat-icon"><Sun size={14}/></span>
          <div>
            <small>This weekend</small>
            <b>{stats.weekend}</b>
            <em>{fmtMatchDate(satIso)} → {fmtMatchDate(sunIso).replace(/^\w{3},\s/, "")}</em>
          </div>
        </div>
        <div className="sched-stat sched-stat--green">
          <span className="sched-stat-icon"><MapPin size={14}/></span>
          <div>
            <small>Grounds in use</small>
            <b>{stats.grounds.size}</b>
            <em>{[...stats.grounds].slice(0, 3).join(" · ") || "none yet"}</em>
          </div>
        </div>
      </div>

      {/* ── Filter toolbar ────────────────────────────────────── */}
      <div className="sched-toolbar">
        <div className="sched-filterChips" role="tablist" aria-label="Schedule filter">
          {FILTER_OPTS.map(opt => {
            const Icon = opt.icon;
            const active = filter === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={`sched-filterChip${active ? " sched-filterChip--active" : ""}`}
                onClick={() => setFilter(opt.key)}
              >
                <Icon size={12}/>
                <span>{opt.label}</span>
                <em>{opt.count}</em>
              </button>
            );
          })}
        </div>
        <div className="sched-search">
          <Search size={14}/>
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search team, series, ground…"
            aria-label="Search matches"
          />
          {search && (
            <button
              type="button"
              className="sched-search-clear"
              onClick={() => setSearch("")}
              aria-label="Clear search"
            ><X size={12}/></button>
          )}
        </div>
      </div>

      {/* ── Body: series → cards grid ─────────────────────────── */}
      {loading && (
        <Panel title={<><CalendarDays size={16}/> Loading…</>}>
          <p style={{ color: "var(--muted)" }}>Loading schedule…</p>
        </Panel>
      )}

      {!loading && matches.length === 0 && (
        <Panel title={<><CalendarDays size={16}/> No matches yet</>}>
          <div className="league-annEmpty">
            <CalendarDays size={28}/>
            <p>
              No matches scheduled yet. Use <b>Add Match</b> for a single game or{" "}
              <b>Upload Excel</b> to bulk-import a season.
            </p>
          </div>
        </Panel>
      )}

      {!loading && matches.length > 0 && filtered.length === 0 && (
        <Panel title={<><Filter size={16}/> Nothing to show</>}>
          <div className="league-annEmpty">
            <Filter size={28}/>
            <p>
              No matches match the current filter{search ? ` and search "${search}"` : ""}.
              <br/>
              <button
                type="button"
                className="link"
                style={{marginTop:8, color:"var(--gold, #f7c948)", background:"none", border:0, padding:0, cursor:"pointer", textDecoration:"underline"}}
                onClick={() => { setFilter("all"); setSearch(""); }}
              >Reset filters</button>
            </p>
          </div>
        </Panel>
      )}

      {!loading && matchesBySeries.length > 0 && (
        <div className="sched-seriesList">
          {matchesBySeries.map(([series, rows]) => {
            const tone = toneFor(series);
            const first = rows[0]?.date;
            const last  = rows[rows.length - 1]?.date;
            return (
              <section key={series} className={`sched-series sched-series--${tone}`}>
                <header className={`sched-series-head sched-series-head--${tone}`}>
                  <span className="sched-series-icon"><Trophy size={22}/></span>
                  <div className="sched-series-title">
                    <span className="sched-series-eyebrow">Series</span>
                    <h3>{series}</h3>
                    <small>
                      {rows.length} match{rows.length === 1 ? "" : "es"}
                      {first && last && first !== last && ` · ${fmtMatchDate(first)} → ${fmtMatchDate(last).replace(/^\w{3},\s/, "")}`}
                      {first && last && first === last && ` · ${fmtMatchDate(first)}`}
                    </small>
                  </div>
                  <span className="sched-series-count">{rows.length}</span>
                </header>

                <div className="sched-cardGrid">
                  {rows.map(m => (
                    <MatchCard
                      key={m.id}
                      match={m}
                      onEdit={() => openEdit(m)}
                      onRemove={() => remove(m)}
                      isPast={m.date < today}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {showForm && createPortal((
        <div className="modal" onClick={close}>
          <div className="modalInner" onClick={e => e.stopPropagation()}>
            <button type="button" className="close" onClick={close} aria-label="Close">
              <X size={20}/>
            </button>
            <Panel title={editingId ? "Edit Match" : "Add Match"}>
              <form className="form" onSubmit={submit}>
                <label>Series *</label>
                <input value={form.series}
                  onChange={e => setForm({ ...form, series: e.target.value })}
                  placeholder="e.g. Summer Series 2026"/>

                <div className="two">
                  <div>
                    <label>Date *</label>
                    <input type="date" value={form.date}
                      onChange={e => setForm({ ...form, date: e.target.value })}/>
                  </div>
                  <div>
                    <label>Time (ET)</label>
                    <input type="time" value={form.time}
                      onChange={e => setForm({ ...form, time: e.target.value })}/>
                  </div>
                </div>

                <div className="two">
                  <div>
                    <label>Team One *</label>
                    <input value={form.teamOne}
                      onChange={e => setForm({ ...form, teamOne: e.target.value })}
                      placeholder="e.g. Pitt Gabruz"/>
                  </div>
                  <div>
                    <label>Team Two *</label>
                    <input value={form.teamTwo}
                      onChange={e => setForm({ ...form, teamTwo: e.target.value })}
                      placeholder="e.g. Steel City Strikers"/>
                  </div>
                </div>

                <label>Ground *</label>
                <input list="league-groundSuggestions" value={form.ground}
                  onChange={e => setForm({ ...form, ground: e.target.value })}
                  placeholder="Linbrook, CC-1, CC-2…"/>
                <datalist id="league-groundSuggestions">
                  {GROUND_SUGGESTIONS.map(g => <option key={g} value={g}/>)}
                </datalist>

                <label>Notes</label>
                <textarea rows={2} value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="Optional — internal notes."/>

                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button type="button" className="dark" onClick={close} disabled={busy}>Cancel</button>
                  <button type="submit" className="btn" disabled={busy}>
                    {busy ? "Saving…" : editingId ? "Save Changes" : "Add Match"}
                  </button>
                </div>
              </form>
            </Panel>
          </div>
        </div>
      ), document.body)}

      {preview && createPortal((
        <div className="modal" onClick={() => setPreview(null)}>
          <div className="modalInner league-previewModal" onClick={e => e.stopPropagation()}>
            <button type="button" className="close" onClick={() => setPreview(null)} aria-label="Close">
              <X size={20}/>
            </button>
            <Panel title={`Preview · ${preview.rows.length} row${preview.rows.length === 1 ? "" : "s"} from upload`}>
              <div className="league-previewCtrl">
                <label style={{display:"inline-flex", gap:6, alignItems:"center"}}>
                  <input type="radio" name="uploadMode" value="append"
                    checked={uploadMode === "append"} onChange={() => setUploadMode("append")}
                    style={{width:"auto"}}/>
                  Append to existing matches
                </label>
                <label style={{display:"inline-flex", gap:6, alignItems:"center"}}>
                  <input type="radio" name="uploadMode" value="replace"
                    checked={uploadMode === "replace"} onChange={() => setUploadMode("replace")}
                    style={{width:"auto"}}/>
                  Replace all existing matches
                </label>
                <button type="button" className="btn" onClick={applyUpload}
                  disabled={uploadBusy || !preview.rows.some(r => r.status === "valid")}
                  style={{marginLeft:"auto"}}>
                  {uploadBusy
                    ? "Importing…"
                    : `Import ${preview.rows.filter(r => r.status === "valid").length} match${preview.rows.filter(r => r.status === "valid").length === 1 ? "" : "es"}`}
                </button>
              </div>
              {uploadMsg && <div className="err" style={{marginTop:8}}>{uploadMsg}</div>}

              <div className="league-previewTable">
                <table>
                  <thead>
                    <tr>
                      <th>#</th><th>Series</th><th>Date</th><th>Time</th>
                      <th>Team One</th><th>Team Two</th><th>Ground</th><th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map(r => (
                      <tr key={r.row} className={r.status === "error" ? "league-previewRow--err" : ""}>
                        <td>{r.row}</td>
                        <td>{r.series || "—"}</td>
                        <td>{r.date || "—"}</td>
                        <td>{r.time || "—"}</td>
                        <td>{r.teamOne || "—"}</td>
                        <td>{r.teamTwo || "—"}</td>
                        <td>{r.ground || "—"}</td>
                        <td>
                          {r.status === "valid"
                            ? <span style={{color:"var(--green, #43d893)"}}>OK</span>
                            : <span style={{color:"var(--red, #ff6b6b)"}}>{r.error}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}

// ── MatchCard ────────────────────────────────────────────────────
// Scoreboard-style card: calendar block on the left, teams headline
// down the middle, ground pill under the meta row, edit/delete actions.
function MatchCard({ match, onEdit, onRemove, isPast }) {
  const { day, dow, mon } = splitDate(match.date);
  const tone = toneFor((match.ground || "") + match.id);
  return (
    <article className={`sched-card sched-card--${tone}${isPast ? " sched-card--past" : ""}`}>
      <header className="sched-card-head">
        <div className="sched-card-datebox">
          <span className="sched-card-dow">{dow}</span>
          <b className="sched-card-day">{day}</b>
          <span className="sched-card-mon">{mon}</span>
        </div>
        <div className="sched-card-headMeta">
          {match.time && (
            <span className="sched-card-time">
              <Clock size={11}/> {fmt12(match.time)} ET
            </span>
          )}
          <span className="sched-card-groundPill">
            <MapPin size={11}/> {match.ground}
          </span>
        </div>
        <div className="sched-card-actions">
          <button type="button" className="sched-card-iconBtn"
            onClick={onEdit} title="Edit match" aria-label="Edit match">
            <Pencil size={13}/>
          </button>
          <button type="button" className="sched-card-iconBtn sched-card-iconBtn--danger"
            onClick={onRemove} title="Delete match" aria-label="Delete match">
            <Trash2 size={13}/>
          </button>
        </div>
      </header>

      <div className="sched-card-teams">
        <span className="sched-card-team">{match.teamOne}</span>
        <span className="sched-card-vs">vs</span>
        <span className="sched-card-team">{match.teamTwo}</span>
      </div>

      {match.notes && (
        <p className="sched-card-notes">{match.notes}</p>
      )}

      {isPast && <span className="sched-card-pastRibbon">Past</span>}
    </article>
  );
}
